<script lang="ts" module>
    export type TagOption = { value: string; label: string };
</script>

<script lang="ts">
    import type { HTMLAttributes } from "svelte/elements";
    import { cn, type WithElementRef } from "../../../utils.js";
    import * as Popover from "../popover/index.js";
    import * as Command from "../command/index.js";
    import { Badge } from "../badge/index.js";
    import X from "@lucide/svelte/icons/x";
    import Check from "@lucide/svelte/icons/check";

    type Props = WithElementRef<Omit<HTMLAttributes<HTMLDivElement>, "children">> & {
        value?: string[];
        options?: TagOption[];
        placeholder?: string;
        disabled?: boolean;
        maxTags?: number;
        validate?: (next: string[]) => string[];
        labelOf?: (value: string) => string;
    };

    let {
        ref = $bindable(null),
        value = $bindable([]),
        options,
        placeholder,
        disabled = false,
        maxTags,
        validate,
        labelOf,
        class: className,
        ...restProps
    }: Props = $props();

    let inputEl = $state<HTMLInputElement | null>(null);
    let freeText = $state("");

    const atMax = $derived(maxTags !== undefined && value.length >= maxTags);

    const labelFor = $derived((v: string): string => {
        if (options) {
            const opt = options.find((o) => o.value === v);
            if (opt) {
                return opt.label;
            }
        }
        if (labelOf) {
            return labelOf(v);
        }
        return v;
    });

    function commit(next: string[]) {
        value = validate ? validate(next) : next;
    }

    function addValue(v: string) {
        const trimmed = v.trim();
        if (trimmed.length === 0) {
            return;
        }
        if (value.includes(trimmed)) {
            return;
        }
        if (atMax) {
            return;
        }
        commit([...value, trimmed]);
    }

    function removeValue(v: string) {
        commit(value.filter((x) => x !== v));
    }

    function toggleValue(v: string) {
        if (value.includes(v)) {
            commit(value.filter((x) => x !== v));
            return;
        }
        if (atMax) {
            return;
        }
        commit([...value, v]);
    }

    function onFreeTextKeydown(e: KeyboardEvent) {
        if (disabled) {
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            addValue(freeText);
            freeText = "";
            return;
        }
        if (e.key === "Backspace" && freeText === "" && value.length > 0) {
            e.preventDefault();
            removeValue(value[value.length - 1]!);
        }
    }

    function onBlur() {
        addValue(freeText);
        freeText = "";
    }
</script>

{#if options}
    <Popover.Root>
        <Popover.Trigger {disabled}>
            {#snippet child({ props })}
                <div
                    bind:this={ref}
                    {...props}
                    data-slot="tag-input"
                    class={cn(
                        "border-input bg-background ring-offset-background flex min-h-9 w-full min-w-0 cursor-text flex-wrap items-center gap-1 rounded-md border px-2 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none",
                        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                        "data-[state=open]:border-ring data-[state=open]:ring-ring/50 data-[state=open]:ring-[3px]",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        className,
                    )}
                    {...restProps}
                >
                    {#each value as v (v)}
                        {@render tagBadge(v)}
                    {/each}
                    {#if value.length === 0 && placeholder}
                        <span class="text-muted-foreground px-1">{placeholder}</span>
                    {/if}
                </div>
            {/snippet}
        </Popover.Trigger>
        <Popover.Content class="w-(--bits-popover-anchor-width) p-0" align="start" sideOffset={4}>
            <Command.Root>
                <Command.Input placeholder={placeholder ?? ""} />
                <Command.List>
                    <Command.Empty>No results</Command.Empty>
                    <Command.Group>
                        {#each options as option (option.value)}
                            {@const selected = value.includes(option.value)}
                            <Command.Item
                                value={option.value}
                                keywords={[option.label]}
                                onSelect={() => toggleValue(option.value)}
                            >
                                <div
                                    class={cn(
                                        "border-primary mr-1 flex size-4 items-center justify-center rounded-sm border",
                                        selected ? "bg-primary text-primary-foreground" : "opacity-50",
                                    )}
                                >
                                    {#if selected}
                                        <Check class="size-3 text-primary-foreground" />
                                    {/if}
                                </div>
                                {option.label}
                            </Command.Item>
                        {/each}
                    </Command.Group>
                </Command.List>
            </Command.Root>
        </Popover.Content>
    </Popover.Root>
{:else}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
        bind:this={ref}
        data-slot="tag-input"
        class={cn(
            "border-input bg-background ring-offset-background flex min-h-9 w-full min-w-0 cursor-text flex-wrap items-center gap-1 rounded-md border px-2 py-1 text-sm shadow-xs transition-[color,box-shadow]",
            "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
            disabled && "cursor-not-allowed opacity-50",
            className,
        )}
        onclick={(e) => {
            if (e.target === e.currentTarget) {
                inputEl?.focus();
            }
        }}
        {...restProps}
    >
        {#each value as v (v)}
            {@render tagBadge(v)}
        {/each}
        <input
            bind:this={inputEl}
            bind:value={freeText}
            type="text"
            {disabled}
            placeholder={value.length === 0 ? placeholder : ""}
            class="placeholder:text-muted-foreground min-w-20 flex-1 bg-transparent px-1 py-0.5 outline-none disabled:cursor-not-allowed"
            onkeydown={onFreeTextKeydown}
            onblur={onBlur}
        />
    </div>
{/if}

{#snippet tagBadge(v: string)}
    <Badge variant="secondary" class="relative group/tag gap-1 py-0.5 pl-2 !overflow-visible">
        <span>{labelFor(v)}</span>
        <button
            type="button"
            tabindex={-1}
            {disabled}
            aria-label="Remove {labelFor(v)}"
            class="text-muted-foreground hover:bg-muted-foreground/20 hover:text-foreground focus-visible:ring-ring absolute -top-1 -right-1 inline-flex size-4 items-center justify-center rounded-full opacity-0 transition-opacity group-hover/tag:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed"
            onclick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                removeValue(v);
            }}
            onkeydown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    removeValue(v);
                }
            }}
        >
            <X class="size-3" />
        </button>
    </Badge>
{/snippet}
